import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProjects, useProjectComparison } from '../hooks/useProjects';
import ProjectStatusBadge from '../components/ProjectStatusBadge';
import BarChart from '../components/charts/BarChart';
import LineChart from '../components/charts/LineChart';

function ProjectComparison() {
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'charts', 'trends'

  const { data: projectsData } = useProjects({
    sort: '-created_at',
  });

  const { data: comparisonData, isLoading: comparing } = useProjectComparison(
    selectedProjects,
    { metrics: 'all' },
    { enabled: selectedProjects.length >= 2 }
  );

  const handleToggleProject = (projectId) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      } else {
        if (prev.length >= 5) {
          toast.error('最多只能比較 5 個專案');
          return prev;
        }
        return [...prev, projectId];
      }
    });
  };

  const projects = projectsData?.data || [];

  // 準備圖表數據
  const chartData = useMemo(() => {
    if (!comparisonData?.data?.projects) return null;

    const projectsList = comparisonData.data.projects;

    // 完成率比較
    const completionRateData = {
      labels: projectsList.map(p => p.code || p.name),
      datasets: [
        {
          label: '完成率 (%)',
          data: projectsList.map(p => p.metrics?.completion_rate || 0),
          backgroundColor: 'rgba(147, 51, 234, 0.5)',
          borderColor: 'rgba(147, 51, 234, 1)',
          borderWidth: 1,
        },
      ],
    };

    // 多指標雷達圖數據
    const metricsData = {
      labels: projectsList.map(p => p.code || p.name),
      datasets: [
        {
          label: '表單數',
          data: projectsList.map(p => p.metrics?.forms || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderColor: 'rgba(59, 130, 246, 1)',
        },
        {
          label: '報價數',
          data: projectsList.map(p => p.metrics?.responses || 0),
          backgroundColor: 'rgba(16, 185, 129, 0.5)',
          borderColor: 'rgba(16, 185, 129, 1)',
        },
        {
          label: '廠商數',
          data: projectsList.map(p => p.metrics?.vendors || 0),
          backgroundColor: 'rgba(245, 158, 11, 0.5)',
          borderColor: 'rgba(245, 158, 11, 1)',
        },
      ],
    };

    // 回覆時間比較
    const responseTimeData = {
      labels: projectsList.map(p => p.code || p.name),
      datasets: [
        {
          label: '平均回覆時間 (天)',
          data: projectsList.map(p => p.metrics?.response_time || 0),
          backgroundColor: 'rgba(239, 68, 68, 0.5)',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
        },
      ],
    };

    return {
      completionRate: completionRateData,
      metrics: metricsData,
      responseTime: responseTimeData,
    };
  }, [comparisonData]);

  // 計算統計摘要
  const statistics = useMemo(() => {
    if (!comparisonData?.data?.projects) return null;

    const projectsList = comparisonData.data.projects;
    const completionRates = projectsList.map(p => p.metrics?.completion_rate || 0);
    const responseTimes = projectsList.map(p => p.metrics?.response_time || 0).filter(t => t > 0);

    return {
      avgCompletionRate: (completionRates.reduce((a, b) => a + b, 0) / completionRates.length).toFixed(1),
      maxCompletionRate: Math.max(...completionRates).toFixed(1),
      minCompletionRate: Math.min(...completionRates).toFixed(1),
      avgResponseTime: responseTimes.length > 0
        ? (responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length).toFixed(1)
        : 'N/A',
      totalForms: projectsList.reduce((sum, p) => sum + (p.metrics?.forms || 0), 0),
      totalResponses: projectsList.reduce((sum, p) => sum + (p.metrics?.responses || 0), 0),
      totalVendors: projectsList.reduce((sum, p) => sum + (p.metrics?.vendors || 0), 0),
    };
  }, [comparisonData]);

  return (
    <div className="min-h-screen bg-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/projects"
          className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          返回專案列表
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">專案比較分析</h1>
          <p className="text-gray-600">選擇 2-5 個專案進行多維度績效比較與視覺化分析</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 專案選擇列表 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-20">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  選擇專案
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  已選 {selectedProjects.length}/5
                </p>
              </div>

              <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
                {projects.map((project) => (
                  <label
                    key={project.id}
                    className={`flex items-start p-3 border rounded-lg cursor-pointer transition ${
                      selectedProjects.includes(project.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.id)}
                      onChange={() => handleToggleProject(project.id)}
                      className="mt-1 mr-3 h-4 w-4 text-purple-600 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {project.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500 font-mono">{project.code}</p>
                        <ProjectStatusBadge status={project.status} size="sm" />
                      </div>
                    </div>
                  </label>
                ))}

                {projects.length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    暫無專案
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 比較結果 */}
          <div className="lg:col-span-3">
            {selectedProjects.length < 2 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">開始比較專案</h3>
                <p className="mt-2 text-sm text-gray-500">
                  請從左側選擇至少 2 個專案進行比較分析
                </p>
                <div className="mt-6">
                  <div className="inline-flex items-center px-4 py-2 bg-purple-50 rounded-lg">
                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-purple-700">支援表格、圖表和趨勢視圖</span>
                  </div>
                </div>
              </div>
            ) : comparing ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
                <p className="mt-4 text-gray-600">正在分析專案數據...</p>
              </div>
            ) : comparisonData ? (
              <div className="space-y-6">
                {/* 視圖切換 */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        viewMode === 'table'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📊 表格視圖
                    </button>
                    <button
                      onClick={() => setViewMode('charts')}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        viewMode === 'charts'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📈 圖表分析
                    </button>
                    <button
                      onClick={() => setViewMode('summary')}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        viewMode === 'summary'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📋 統計摘要
                    </button>
                  </div>
                </div>

                {/* 表格視圖 */}
                {viewMode === 'table' && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden fade-in">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
                      <h2 className="text-lg font-semibold text-gray-900">績效比較表</h2>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              專案
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              狀態
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              表單數
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              報價數
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              廠商數
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              完成率
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                              平均回覆時間
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {comparisonData.data?.projects?.map((project) => (
                            <tr key={project.id} className="hover:bg-gray-50 transition">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {project.name}
                                </div>
                                <div className="text-sm text-gray-500 font-mono">{project.code}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <ProjectStatusBadge status={project.status} />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                                {project.metrics?.forms || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                                {project.metrics?.responses || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                                {project.metrics?.vendors || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full ${
                                        (project.metrics?.completion_rate || 0) >= 80
                                          ? 'bg-green-500'
                                          : (project.metrics?.completion_rate || 0) >= 50
                                          ? 'bg-yellow-500'
                                          : 'bg-red-500'
                                      }`}
                                      style={{ width: `${project.metrics?.completion_rate || 0}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {project.metrics?.completion_rate || 0}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                {project.metrics?.response_time
                                  ? `${project.metrics.response_time.toFixed(1)} 天`
                                  : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 圖表視圖 */}
                {viewMode === 'charts' && chartData && (
                  <div className="space-y-6 fade-in">
                    {/* 完成率比較 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">完成率比較</h3>
                      <BarChart data={chartData.completionRate} />
                    </div>

                    {/* 多指標比較 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">多維度指標比較</h3>
                      <BarChart data={chartData.metrics} stacked={false} />
                    </div>

                    {/* 回覆時間比較 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">平均回覆時間比較</h3>
                      <p className="text-sm text-gray-500 mb-4">越低越好</p>
                      <BarChart data={chartData.responseTime} />
                    </div>
                  </div>
                )}

                {/* 統計摘要視圖 */}
                {viewMode === 'summary' && statistics && (
                  <div className="space-y-6 fade-in">
                    {/* 關鍵指標卡片 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-medium opacity-90">平均完成率</h3>
                          <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <p className="text-4xl font-bold">{statistics.avgCompletionRate}%</p>
                        <p className="text-sm mt-2 opacity-90">
                          範圍: {statistics.minCompletionRate}% - {statistics.maxCompletionRate}%
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-medium opacity-90">平均回覆時間</h3>
                          <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-4xl font-bold">
                          {typeof statistics.avgResponseTime === 'number'
                            ? `${statistics.avgResponseTime}`
                            : statistics.avgResponseTime}
                        </p>
                        <p className="text-sm mt-2 opacity-90">
                          {typeof statistics.avgResponseTime === 'number' ? '天' : ''}
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-medium opacity-90">總專案數</h3>
                          <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                        <p className="text-4xl font-bold">{selectedProjects.length}</p>
                        <p className="text-sm mt-2 opacity-90">正在比較中</p>
                      </div>
                    </div>

                    {/* 總計統計 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">總計統計</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600">{statistics.totalForms}</div>
                          <div className="text-sm text-gray-600 mt-1">總表單數</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600">{statistics.totalResponses}</div>
                          <div className="text-sm text-gray-600 mt-1">總報價數</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-purple-600">{statistics.totalVendors}</div>
                          <div className="text-sm text-gray-600 mt-1">總廠商數</div>
                        </div>
                      </div>
                    </div>

                    {/* 最佳表現 */}
                    {comparisonData.data?.comparison && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 最佳表現</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {comparisonData.data.comparison.best_completion_rate && (
                            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-xl">
                                  ✓
                                </div>
                                <div>
                                  <p className="text-sm text-green-700 font-medium">最佳完成率</p>
                                  <p className="text-lg font-bold text-green-900">
                                    {
                                      comparisonData.data.projects.find(
                                        (p) => p.id === comparisonData.data.comparison.best_completion_rate
                                      )?.name
                                    }
                                  </p>
                                  <p className="text-sm text-green-600">
                                    {
                                      comparisonData.data.projects.find(
                                        (p) => p.id === comparisonData.data.comparison.best_completion_rate
                                      )?.metrics?.completion_rate
                                    }% 完成率
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {comparisonData.data.comparison.fastest_response && (
                            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl">
                                  ⚡
                                </div>
                                <div>
                                  <p className="text-sm text-blue-700 font-medium">最快回覆</p>
                                  <p className="text-lg font-bold text-blue-900">
                                    {
                                      comparisonData.data.projects.find(
                                        (p) => p.id === comparisonData.data.comparison.fastest_response
                                      )?.name
                                    }
                                  </p>
                                  <p className="text-sm text-blue-600">
                                    平均 {
                                      comparisonData.data.projects.find(
                                        (p) => p.id === comparisonData.data.comparison.fastest_response
                                      )?.metrics?.response_time?.toFixed(1)
                                    } 天回覆
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 專案詳細列表 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">專案詳細資訊</h3>
                      <div className="space-y-4">
                        {comparisonData.data?.projects?.map((project, index) => (
                          <div key={project.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold">
                                  {index + 1}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900">{project.name}</h4>
                                  <p className="text-sm text-gray-500 font-mono">{project.code}</p>
                                </div>
                              </div>
                              <ProjectStatusBadge status={project.status} />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">表單:</span>
                                <span className="ml-2 font-semibold text-gray-900">{project.metrics?.forms || 0}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">報價:</span>
                                <span className="ml-2 font-semibold text-gray-900">{project.metrics?.responses || 0}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">廠商:</span>
                                <span className="ml-2 font-semibold text-gray-900">{project.metrics?.vendors || 0}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">完成:</span>
                                <span className="ml-2 font-semibold text-gray-900">{project.metrics?.completion_rate || 0}%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectComparison;
